/**
 * Edit in place. A near-miss item is worth more repaired than discarded, so the desk
 * lets the reviewer fix the stem, the options, the key, the misconceptions and the
 * rationales rather than only pass judgement.
 *
 * Validation mirrors the backend so the reviewer gets an answer while typing, and the
 * Save control stays disabled until the item is one the writer could legally have
 * produced. The backend re-checks everything; this is courtesy, not authority.
 */
import React, { useMemo, useState } from 'react';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { ItemDetail, OptionEdit, ItemEditPayload, PATIENT_FIELDS } from '../../../inbde/reviewTypes';
import { validateOptions, stemOf } from '../../../inbde/reviewRules';
import { Eyebrow, Field, ErrorNote } from './primitives';

const LETTERS = ['A', 'B', 'C', 'D', 'E'];

interface Props {
  item: ItemDetail;
  saving: boolean;
  error: string | null;
  onCancel: () => void;
  onSave: (payload: ItemEditPayload) => void;
}

const ItemEditor: React.FC<Props> = ({ item, saving, error, onCancel, onSave }) => {
  const [stem, setStem] = useState(() => stemOf(item.statement_blocks));
  const [box, setBox] = useState<Record<string, string>>(() => ({ ...(item.patient_box || {}) }));
  const [options, setOptions] = useState<OptionEdit[]>(() => item.options.map((o) => ({
    id: o.id, text: o.text, is_key: o.is_key,
    concept_id: o.concept_id, misconception: o.misconception, rationale_md: o.rationale_md,
  })));
  const [notes, setNotes] = useState('');

  const problems = useMemo(() => {
    const p = validateOptions(options);
    if (!stem.trim()) p.unshift('The stem cannot be empty.');
    return p;
  }, [options, stem]);

  const patch = (i: number, next: Partial<OptionEdit>) =>
    setOptions((prev) => prev.map((o, idx) => (idx === i ? { ...o, ...next } : o)));

  const setKey = (i: number) =>
    setOptions((prev) => prev.map((o, idx) => ({ ...o, is_key: idx === i })));

  const addOption = () => setOptions((prev) => (prev.length >= 5 ? prev : [...prev, {
    id: LETTERS[prev.length] || String(prev.length + 1), text: '', is_key: false,
    concept_id: null, misconception: '', rationale_md: '',
  }]));

  const removeOption = (i: number) => setOptions((prev) => (prev.length <= 3 ? prev : prev.filter((_, idx) => idx !== i)));

  const submit = () => {
    if (problems.length > 0) return;
    const payload: ItemEditPayload = { stem_md: stem.trim(), options, notes: notes.trim() || null };
    if (item.patient_box) payload.patient_box = box;
    onSave(payload);
  };

  return (
    <div className="flex flex-col gap-6" aria-label="Edit item">
      <ErrorBoundaryless error={error} />

      <Field label="Stem" hint="What the candidate is asked. Keep it answerable without seeing the options.">
        <textarea
          className="in-textarea in-focus"
          value={stem}
          onChange={(e) => setStem(e.target.value)}
          rows={3}
          style={{ fontSize: 15, lineHeight: 1.6 }}
        />
      </Field>

      {item.patient_box && (
        <div className="flex flex-col gap-3">
          <Eyebrow>Patient</Eyebrow>
          <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
            {PATIENT_FIELDS.map((f) => (
              <Field key={f.key} label={f.label}>
                <textarea
                  className="in-textarea in-focus"
                  rows={3}
                  value={box[f.key] || ''}
                  onChange={(e) => setBox((prev) => ({ ...prev, [f.key]: e.target.value }))}
                />
              </Field>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <Eyebrow>Options</Eyebrow>
          <span className="text-[11.5px]" style={{ color: 'var(--text-tertiary)' }}>
            Three to five. Exactly one correct.
          </span>
        </div>

        <div className="flex flex-col gap-3">
          {options.map((o, i) => (
            <div
              key={i}
              className="flex flex-col gap-3 p-3.5"
              style={{
                border: `1.5px solid ${o.is_key ? 'var(--in-correct)' : 'var(--in-line)'}`,
                borderRadius: 'var(--in-r-surface)',
                background: o.is_key ? 'var(--in-correct-bg)' : 'var(--bg-secondary)',
              }}
            >
              <div className="flex items-center gap-3">
                <input
                  className="in-input in-focus in-mono"
                  style={{ width: 52, textAlign: 'center' }}
                  value={o.id}
                  maxLength={4}
                  onChange={(e) => patch(i, { id: e.target.value.toUpperCase() })}
                  aria-label={`Letter for option ${i + 1}`}
                />
                <label className="flex items-center gap-2 text-[12.5px] cursor-pointer" style={{ color: 'var(--text-secondary)' }}>
                  <input
                    type="radio"
                    name="correct-option"
                    className="in-focus"
                    checked={o.is_key}
                    onChange={() => setKey(i)}
                    style={{ accentColor: 'var(--in-correct)' }}
                  />
                  Correct answer
                </label>
                <button
                  type="button"
                  className="in-btn in-focus ml-auto"
                  style={{ padding: '7px 9px' }}
                  onClick={() => removeOption(i)}
                  disabled={options.length <= 3}
                  aria-label={`Remove option ${o.id}`}
                  title={options.length <= 3 ? 'An item needs at least three options' : 'Remove this option'}
                >
                  <TrashIcon className="w-4 h-4" aria-hidden="true" />
                </button>
              </div>

              <textarea
                className="in-textarea in-focus"
                rows={2}
                value={o.text}
                placeholder="Option text"
                onChange={(e) => patch(i, { text: e.target.value })}
                aria-label={`Text for option ${o.id}`}
              />

              {!o.is_key && (
                <Field
                  label="Misconception"
                  hint="What does a candidate who picks this believe? This becomes the explanation they read."
                >
                  <input
                    className="in-input in-focus"
                    value={o.misconception || ''}
                    placeholder="Reads percussion tenderness as the primary finding"
                    onChange={(e) => patch(i, { misconception: e.target.value })}
                  />
                </Field>
              )}

              <Field label={o.is_key ? 'Why this is right' : 'Why this is wrong'}>
                <textarea
                  className="in-textarea in-focus"
                  rows={2}
                  value={o.rationale_md || ''}
                  onChange={(e) => patch(i, { rationale_md: e.target.value })}
                />
              </Field>
            </div>
          ))}
        </div>

        <button type="button" className="in-btn in-focus self-start" onClick={addOption} disabled={options.length >= 5}>
          <PlusIcon className="w-4 h-4" aria-hidden="true" />
          Add option
        </button>
      </div>

      <Field label="Note on this edit" hint="Optional. Recorded against your name in the item history.">
        <input
          className="in-input in-focus"
          value={notes}
          placeholder="Tightened the stem and named the distractor for C"
          onChange={(e) => setNotes(e.target.value)}
        />
      </Field>

      {problems.length > 0 && (
        <ul
          className="flex flex-col gap-1.5 p-3.5 text-[12.5px] leading-relaxed"
          style={{
            background: 'var(--in-flagged-bg)',
            border: '1px solid var(--in-flagged-line)',
            borderRadius: 'var(--in-r-surface)',
            color: 'var(--in-flagged)',
          }}
        >
          {problems.map((p) => <li key={p}>{p}</li>)}
        </ul>
      )}

      <div className="flex items-center gap-3 pt-4" style={{ borderTop: '1px solid var(--in-line)' }}>
        <button
          type="button"
          className="in-btn in-btn--primary in-focus"
          onClick={submit}
          disabled={problems.length > 0 || saving}
        >
          {saving ? 'Saving' : 'Save changes'}
        </button>
        <button type="button" className="in-btn in-focus" onClick={onCancel} disabled={saving}>
          Discard
        </button>
        <span className="text-[11.5px] ml-auto" style={{ color: 'var(--text-tertiary)' }}>
          Saving records what changed against your name.
        </span>
      </div>
    </div>
  );
};

/** Small wrapper so the error slot renders nothing rather than an empty box. */
const ErrorBoundaryless: React.FC<{ error: string | null }> = ({ error }) =>
  (error ? <ErrorNote message={error} /> : null);

export default ItemEditor;
